import { Component, inject, OnDestroy, OnInit } from "@angular/core";
import { DynamicFormService } from "../../../../../../shared/components/dynamic-forms-components/dynamic-form.service";
import {
  FormArray,
  FormBuilder,
  FormGroup,
  UntypedFormGroup,
  Validators,
} from "@angular/forms";
import { FieldConfig } from "../../../../../../shared/components/dynamic-forms-components/field.interface";
import * as fromConfigs from "./warehouse-sensor-alert.form";
import { AppState } from "../../../../../../store";
import { Store } from "@ngrx/store";
import { StoreItemActions } from "../../../../../../store/entities/store-item/store-item.actions";
import { StoreItemEntity } from "../../../../../../store/entities/store-item/store-item.model";
import { ActivatedRoute } from "@angular/router";
import {
  CREATE_SENSOR_ALERT_CONFIG_MUTATION,
  GET_SENSOR_ALERT_CONFIG_QUERY,
  UPDATE_SENSOR_ALERT_CONFIG_MUTATION,
} from "../../../../../../store/entities/warehouse-management/sensor-alert-configuration/sensor-alert-configuration.graphql";
import {
  NotificationEscalationLevel,
  SensorAlertConfigInput,
} from "../../../../../../store/entities/warehouse-management/sensor-alert-configuration/sensor-alert-configuration.model";
import { GET_ASSIGNED_PERSON_IN_WAREHOUSE } from "../../../../../../store/entities/warehouse-management/warehouse-assignee/warehouse-assignee.graphql";
import { Observable, Subscription } from "rxjs";
import { selectStoreItems } from "../../../../../../store/entities/store-item/store-item.selectors";
import { GraphqlService } from "../../../../../../services/graphql.service";

@Component({
  selector: "app-zone-sensor-alert",
  templateUrl: "./warehouse-sensor-alert.component.html",
  styleUrl: "./warehouse-sensor-alert.component.scss",
})
export class WarehouseSensorAlertComponent implements OnInit {
  private dynamicFormService: DynamicFormService = inject(DynamicFormService);
  private apollo: GraphqlService = inject(GraphqlService);
  private data;
  selectedEsc = 0;
  alertConfigForm: FormGroup;
  alertConfig$: Observable<any>;
  supportForm: UntypedFormGroup;
  validationError: boolean = false;
  sensorAlertConfigFields: FieldConfig[] = fromConfigs.sensorAlertConfigFields;
  title: string;
  loading: boolean = false;
  asignees$: Observable<any[]>;
  loadingAssignee: boolean = false;

  escalationLeveles = [
    {
      id: 1,
      name: "First Responder",
      value: NotificationEscalationLevel.FIRST_RESPONDER,
    },
    {
      id: 2,
      name: "Second Responder",
      value: NotificationEscalationLevel.SECOND_RESPONDER,
    },
    {
      id: 3,
      name: "Third Responder",
      value: NotificationEscalationLevel.THIRD_RESPONDER,
    },
    {
      id: 4,
      name: "Fourth Responder",
      value: NotificationEscalationLevel.FOURTH_RESPONDER,
    },
    {
      id: 5,
      name: "Fifth Responder",
      value: NotificationEscalationLevel.FIFTH_RESPONDER,
    },
  ];
  // onEscSelectionChange(event: any): void {
  //   this.selectedEsc = event.value;
  // }

  constructor(
    private store: Store<AppState>,
    private route: ActivatedRoute,
    private fb: FormBuilder,
  ) {
    this.alertConfigForm = fb.group({
      uniqueId: [null],
      maxTemperature: [null, Validators.required],
      minTemperature: [null, Validators.required],
      maxHumidity: [null, Validators.required],
      minHumidity: [null, Validators.required],
      occurenceCircles: [null, Validators.required],
      circlesToEscalation: [null, Validators.required],
      escalationPeriod: [null, Validators.min(10)],
      alertReceivers: fb.array([]),
    });

    this.alertConfigForm.get("circlesToEscalation")?.valueChanges.subscribe(
      (value: number) => {
        this.updateAlertReceivers(value);
      },
    );
  }
  private updateAlertReceivers(selectedEscalation: number) {
    const alertReceivers = this.alertConfigForm.get(
      "alertReceivers",
    ) as FormArray;
    alertReceivers.clear();

    for (let i = 0; i < selectedEscalation; i++) {
      alertReceivers.push(this.fb.group({
        level: [this.escalationLeveles[i].value],
        receiver: [null, Validators.required],
      }));
    }
  }

  async fetchFromApollo() {
    const result: any = await this.apollo.queryNoLoader({
      query: GET_SENSOR_ALERT_CONFIG_QUERY,
      variables: {
        filtering: { sensor: this.route.snapshot.queryParams["sensor"] },
      },
    });
    const { data, response } = result.data.getSensorAlertsConfig;
    if (response.status) {
      data?.map((config) => {
        this.alertConfigForm.patchValue({
          uniqueId: config.uniqueId,
          maxTemperature: config.maxTemperature,
          minTemperature: config.minTemperature,
          maxHumidity: config.maxHumidity,
          minHumidity: config.minHumidity,
          occurenceCircles: config.occurenceCircles,
          circlesToEscalation: config.circlesToEscalation,
          escalationPeriod: config.escalationPeriod,
        });

        const alertReceivers = this.alertConfigForm.get(
          "alertReceivers",
        ) as FormArray;
        alertReceivers.clear();

        config.alertReceivers.forEach((receiver: any, index: number) => {
          alertReceivers.push(this.fb.group({
            level: [receiver.level],
            receiver: [receiver.receiver],
          }));
        });
      });
    } else {
      this.alertConfigForm.reset();
    }
  }

  ngOnInit(): void {
    this.fetchWarehouseUsers();
    this.supportForm = this.dynamicFormService.createControl(
      this.sensorAlertConfigFields,
      this.data,
    );
    if (this.route.snapshot.queryParams["sensor"]) {
      this.fetchFromApollo();
    }
  }
  async fetchWarehouseUsers() {
    this.loadingAssignee = true;
    try {
      await this.store.dispatch(
        StoreItemActions.getStoreItems({
          entity: StoreItemEntity.warehouseAssignee,
          filtering: {
            warehouseUniqueId: this.route.snapshot.queryParams["warehouse"],
          },
          query: GET_ASSIGNED_PERSON_IN_WAREHOUSE,
        }),
      );
      this.asignees$ = this.store.select(
        selectStoreItems(StoreItemEntity.warehouseAssignee),
      );
      this.loadingAssignee = false;
    } catch (e) {
      this.loadingAssignee = false;
    } finally {
    }
  }

  submitForm() {
    this.loading = true;
    this.validationError = false;
    if (!this.alertConfigForm.valid) {
      this.validationError = true;
      return;
    }

    const input: SensorAlertConfigInput = {
      sensor: this.route.snapshot.queryParams["sensor"],
      ...this.alertConfigForm.value,
      alertReceivers: this.formatReceivers(
        this.alertConfigForm.value.alertReceivers,
      ),
    };

    this.store.dispatch(StoreItemActions.createUpdateStoreItem({
      entity: StoreItemEntity.sensorAlertConfig,
      mutation: this.alertConfigForm.value.uniqueId
        ? UPDATE_SENSOR_ALERT_CONFIG_MUTATION
        : CREATE_SENSOR_ALERT_CONFIG_MUTATION,
      input: input,
      isInput: true,
    }));
  }

  private formatReceivers(
    receivers: any[],
  ): { level: string; receiver: string }[] {
    return receivers.map((receiverGroup: any, index: number) => ({
      level: this.escalationLeveles[index].value,
      receiver: receiverGroup.receiver,
    }));
  }
}
